import { cloneDeep } from "lodash";
import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { GROWL_ACTIONS } from "PFCore/reducers/growl_reducer";
import { ApiRoute } from "PFCore/utilities/routes";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import css from "./change_request_page.less";
import About from "./parts/about";
import Avatar from "./parts/avatar";
import Positions from "./parts/positions";

const ChangeRequestPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const [changeRequest, setChangeRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPending, setIsPending] = useState(false);
  const [errors, setErrors] = useState({});
  const { dispatch } = useContext(AppContext);

  const { t } = useTranslation("changeRequests");
  const { formatDate } = useDateFormatter();

  const [disabledItems, setDisabledItems] = useState({
    customFields: {},
    positions: {}
  });

  useEffect(() => {
    if (id) {
      $.get({ url: ApiRoute(`/api/change_requests/${id}`), api_version: 2 })
        .then((resp) => {
          setChangeRequest(resp);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  }, [id]);

  const mergeData = (change) =>
    setChangeRequest({ ...changeRequest, data: { ...changeRequest.data, ...change } });

  const commonProps = {
    data: changeRequest?.data,
    mergeData,
    disabledItems,
    setDisabledItems
  };

  const save = () => {
    if (!changeRequest) {
      return;
    }

    setIsPending(true);

    const data = cloneDeep(changeRequest.data);

    disabledItems.avatar && delete data.avatar;
    disabledItems.first_name && delete data.first_name;
    disabledItems.last_name && delete data.last_name;
    disabledItems.summary && delete data.summary;

    data.custom_fields = data.custom_fields?.filter(({ type }) => !disabledItems.customFields[type.name]);
    if (!data.custom_fields?.length) {
      delete data.custom_fields;
    }

    data.positions = data.positions
      ?.map((position, index) => (disabledItems.positions[index] ? false : position))
      .filter(Boolean);
    if (!data.positions?.length) {
      delete data.positions;
    }

    $.ajax({
      method: "PUT",
      url: ApiRoute(`/api/change_requests/${id}/apply`),
      api_version: 2,
      data: {
        applied_data: JSON.stringify(data)
      }
    })
      .then(() => {
        history.push("/profiles/me");
      })
      .catch((resp) => {
        dispatch({
          type: GROWL_ACTIONS.GROWL_RENDER,
          payload: { kind: "error", message: t("errors.changesNotSaved") }
        });
        setErrors(resp.responseJSON.errors || {});
        setIsPending(false);
      });
  };

  return (
    <div className={css.root} data-qa-id="ChangeRequestPage">
      <h1>{t("reviewYourCV")}</h1>
      {changeRequest && <small className={css.date}>{formatDate(changeRequest.created_at)}</small>}

      {isLoading && <LoadingDots circlesEnabled pageViewEnabled />}
      {!isLoading && !changeRequest && <div className={css.empty}>{t("notFound")}</div>}
      {!isLoading && changeRequest && (
        <div style={isPending ? { opacity: 0.6, pointerEvents: "none" } : null}>
          <Avatar {...commonProps} />
          <About {...commonProps} errors={errors.profile?.[0]?.errors || {}} />
          <Positions {...commonProps} errors={errors.positions || []} />

          <div className={css.buttonWrap}>
            <Button onClick={save} qaId="ChangeRequestPage.confirm">
              {t("confirmData")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeRequestPage;
